import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { type TipperEmployeeProfile } from '../../../shared/api/tipper.service';
import { TipFlowTopBar } from '../../../shared/ui';
import {
  formatTipCheckoutCurrencyAmount,
  resolveTipCheckoutCountryCode,
  TIP_CHECKOUT_MIN_AMOUNT,
  useTipCheckoutForm,
  useTipCheckoutProfileContent,
} from '../model';

import { TipCheckoutAmountCard } from './tip-checkout-amount-card';
import { TipCheckoutPaymentSection } from './tip-checkout-payment-section';
import { TipCheckoutProfileCard } from './tip-checkout-profile-card';
import { TipCheckoutPromoCard } from './tip-checkout-promo-card';
import { TipCheckoutRatingCard } from './tip-checkout-rating-card';

const Screen = styled.div.attrs({
  'data-ui': 'tips-checkout-screen',
})<{ $hasExpandedFeedback: boolean }>`
  height: 100%;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  display: flex;
  flex-direction: column;

  & > [data-ui='tip-flow-top-bar'] {
    margin-bottom: ${({ theme }) => theme.scale.px(18)};
  }

  & > [data-ui='tips-checkout-promo-card'] {
    margin-bottom: ${({ theme }) => theme.scale.px(8)};
  }

  & > [data-ui='tips-checkout-profile-card'] {
    margin-bottom: ${({ theme }) => theme.scale.px(9)};
  }

  & > [data-ui='tips-checkout-amount-card'] {
    margin-bottom: ${({ theme }) => theme.scale.px(10)};
  }

  & > [data-ui='tips-checkout-payment-section'] {
    margin-top: ${({ $hasExpandedFeedback, theme }) =>
      theme.scale.px($hasExpandedFeedback ? 47 : 36)};

    & > [data-ui='tips-checkout-payment-hint'] {
      margin-top: ${({ theme }) => theme.scale.px(18)};
    }

    & > [data-ui='tips-checkout-payment-methods'] {
      margin-top: ${({ theme }) => theme.scale.px(17)};
    }

    & > [data-ui='tips-checkout-payment-status'] {
      margin-top: ${({ theme }) => theme.scale.px(12)};
    }

    & > [data-ui='tips-checkout-payment-consent'] {
      margin-top: ${({ theme }) => theme.scale.px(16)};
    }

    & > [data-ui='tips-checkout-payment-consent']:first-of-type {
      margin-top: ${({ theme }) => theme.scale.px(60)};
    }
  }
`;

export type TipCheckoutPaymentDraft = {
  amount: number;
  comment: string;
  isFeeIncluded: boolean;
  score: number | null;
};

const noopAmountChange = (_selectedAmount: number) => undefined;
const noopPay = (_paymentDraft: TipCheckoutPaymentDraft) => undefined;

type TipCheckoutScreenProps = {
  initialAmount?: number;
  isPaying?: boolean;
  onAmountChange?: (selectedAmount: number) => void;
  onPay?: (paymentDraft: TipCheckoutPaymentDraft) => void;
  paymentError?: string | null;
  recipientFirstName?: string | null;
  recipientProfile?: TipperEmployeeProfile | null;
  showPromoCard?: boolean;
};

export function TipCheckoutScreen({
  initialAmount,
  isPaying = false,
  onAmountChange = noopAmountChange,
  onPay = noopPay,
  paymentError = null,
  recipientFirstName,
  recipientProfile,
  showPromoCard = false,
}: TipCheckoutScreenProps) {
  const { t, i18n } = useTranslation();
  const {
    avatarUrl,
    feedbackTitle,
    goalText,
    isRatingVisible,
    profileName,
    progressLabel,
    progressPercent,
    ratingValueLabel,
  } = useTipCheckoutProfileContent(recipientProfile);
  const {
    activateManualAmountEntry,
    canPay,
    beginFeedbackEditing,
    completeManualAmountEntry,
    currencyPrefix,
    feedbackCharacterCount,
    feedbackPromptKey,
    feedbackText,
    isConsentAccepted,
    isFeeIncluded,
    isFeedbackPrefilled,
    isManualAmountEntry,
    manualAmountInput,
    presetAmounts,
    selectedAmount,
    selectedAmountLabel,
    selectedRating,
    selectPresetAmount,
    selectRating,
    setConsentAccepted,
    setFeeIncluded,
    shouldShowFeedbackField,
    updateManualAmount,
    updateFeedbackText,
  } = useTipCheckoutForm(t('tipsCheckout.feedbackPlaceholder'), initialAmount);
  const hasMountedRef = useRef(false);
  const countryCode = resolveTipCheckoutCountryCode(i18n.language);
  const minimumAmountStatusMessage =
    selectedAmount !== null && selectedAmount < TIP_CHECKOUT_MIN_AMOUNT
      ? t('tipsCheckout.minimumAmountHint', {
          amount: formatTipCheckoutCurrencyAmount(
            TIP_CHECKOUT_MIN_AMOUNT,
            countryCode,
          ),
        })
      : null;

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }

    if (selectedAmount === null) {
      return;
    }

    onAmountChange(selectedAmount);
  }, [onAmountChange, selectedAmount]);

  return (
    <Screen $hasExpandedFeedback={shouldShowFeedbackField}>
      <TipFlowTopBar />
      {showPromoCard ? <TipCheckoutPromoCard /> : null}
      <TipCheckoutProfileCard
        avatarUrl={avatarUrl}
        goalText={goalText}
        isRatingVisible={isRatingVisible}
        profileName={profileName}
        progressLabel={progressLabel}
        progressPercent={progressPercent}
        ratingValueLabel={ratingValueLabel}
      />
      <TipCheckoutAmountCard
        amountOptions={presetAmounts}
        currencyPrefix={currencyPrefix}
        isManualAmountEntry={isManualAmountEntry}
        manualAmountInput={manualAmountInput}
        selectedAmount={selectedAmount}
        selectedAmountLabel={selectedAmountLabel}
        onAmountSelect={selectPresetAmount}
        onManualAmountActivate={activateManualAmountEntry}
        onManualAmountChange={updateManualAmount}
        onManualAmountSubmit={completeManualAmountEntry}
      />
      <TipCheckoutRatingCard
        feedbackCharacterCount={feedbackCharacterCount}
        feedbackPromptKey={feedbackPromptKey}
        feedbackText={feedbackText}
        feedbackTitle={feedbackTitle}
        isFeedbackPrefilled={isFeedbackPrefilled}
        selectedRating={selectedRating}
        shouldShowFeedbackField={shouldShowFeedbackField}
        onFeedbackChange={updateFeedbackText}
        onFeedbackFocus={beginFeedbackEditing}
        onRatingSelect={selectRating}
      />
      <TipCheckoutPaymentSection
        canPay={canPay}
        isConsentAccepted={isConsentAccepted}
        isFeeIncluded={isFeeIncluded}
        isSubmitting={isPaying}
        isStatusError={paymentError !== null}
        onConsentChange={setConsentAccepted}
        onFeeIncludedChange={setFeeIncluded}
        onPay={() => {
          if (selectedAmount === null) {
            return;
          }

          onPay({
            amount: selectedAmount,
            comment: isFeedbackPrefilled ? '' : feedbackText,
            isFeeIncluded,
            score: selectedRating,
          });
        }}
        recipientFirstName={recipientFirstName}
        statusMessage={paymentError ?? minimumAmountStatusMessage}
      />
    </Screen>
  );
}
